import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PencilCircle, VideoCamera, SquaresFour, EnvelopeSimple,
  UserCircle, ChatCircleDots, Newspaper, Sparkle, MagnifyingGlass, PenNib, Rocket, Camera, Palette, Briefcase
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import ToolPreview from '../components/ToolPreview';

/* ── 5 categories ─────────────────────────────────────────────── */
const SECTIONS = [
  {
    id: 'quick-win',
    label: 'Quick Win',
    sub: 'One link. A full month of ready-to-post content.',
    tools: [
      { path: '/dashboard/strategy', preview: 'auto-campaign', icon: Briefcase, label: 'Weekly Strategy', desc: 'Brand audit + this week\'s campaign deck', credits: null, featured: true, badge: 'NEW' },
      { path: '/dashboard/auto-campaign', preview: 'auto-campaign', icon: Rocket, label: 'Auto Campaign', desc: 'Paste a link, get a month of content', credits: null },
    ],
  },
  {
    id: 'write',
    label: 'Write',
    sub: 'In your voice. In minutes.',
    tools: [
      { path: '/dashboard/content-studio', preview: 'content-studio', icon: PencilCircle, label: 'Content Studio', desc: 'Captions, scripts, and hooks', credits: null },
      { path: '/dashboard/email', preview: 'email-campaigns', icon: EnvelopeSimple, label: 'Email Campaigns', desc: 'Full email sequences', credits: null },
      { path: '/dashboard/voiceover', preview: 'voiceover', icon: ChatCircleDots, label: 'Voiceover', desc: 'Professional voice audio', credits: 1 },
      { path: '/dashboard/copy-studio', preview: 'template-studio', icon: Sparkle, label: 'Copy Studio', desc: 'Ad copy, headlines, sales writing', credits: null },
      { path: '/dashboard/blog-post', preview: 'blog-post', icon: Newspaper, label: 'Blog Post', desc: 'SEO posts that grow visibility', credits: 3 },
    ],
  },
  {
    id: 'design',
    label: 'Design',
    sub: 'Agency-quality creative. Directed for you.',
    tools: [
      { path: '/dashboard/creative-studio', preview: '3d-visual-ads', icon: Palette, label: 'Creative Studio', desc: 'AI creative director · directs the whole campaign', credits: null, featured: true },
      { path: '/dashboard/visual-ads', preview: '3d-visual-ads', icon: Sparkle, label: '3D Visual Ads', desc: 'Premium branded ads', credits: 6 },
      { path: '/dashboard/carousel-creator', preview: 'carousels', icon: SquaresFour, label: 'Carousels', desc: 'Multi-slide post designs', credits: 2 },
      { path: '/dashboard/flyer-creator', preview: 'flyer-creator', icon: Newspaper, label: 'Flyer & Graphics', desc: 'Promotional graphics', credits: null },
      { path: '/dashboard/character-creator', preview: 'character-creator', icon: UserCircle, label: 'Character Creator', desc: 'Brand character in 12 styles', credits: 1, pro: true },
    ],
  },
  {
    id: 'video',
    label: 'Video',
    sub: 'Motion content that gets attention.',
    tools: [
      { path: '/dashboard/ad-creator', preview: 'video-ads', icon: VideoCamera, label: 'Video Ads', desc: 'Short-form video ads', credits: 8 },
      { path: '/dashboard/animation-series', preview: 'animation-series', icon: VideoCamera, label: 'Animation Series', desc: 'Branded short-form series', credits: 3 },
      { path: '/dashboard/product-shoot', preview: 'product-shoot-studio', icon: Camera, label: 'Product Shoot Studio', desc: 'Studio shots from any photo', credits: 5 },
    ],
  },
  {
    id: 'web',
    label: 'Web',
    sub: 'Get discovered online.',
    tools: [
      { path: '/dashboard/launch-page', preview: 'landing-page', icon: Rocket, label: 'Landing Page', desc: 'Branded, shareable URL', credits: 5 },
      { path: '/dashboard/case-study', preview: 'case-studies', icon: PenNib, label: 'Case Studies', desc: 'Agency-grade proof that converts', credits: 3 },
      { path: '/dashboard/faq', preview: 'faq-pages', icon: ChatCircleDots, label: 'FAQ Pages', desc: 'Long-tail SEO + featured snippets', credits: 3 },
    ],
  },
];

const COMING_SOON = [
  { path: '/dashboard/meta-ads', icon: Rocket, label: 'Meta Ads Manager', desc: 'Direct ad publishing to Meta' },
  { path: '/dashboard/content-calendar', icon: Newspaper, label: 'Auto-Scheduling', desc: 'Set & forget posting schedule' },
];

const ALL_TOOLS = SECTIONS.flatMap(s => s.tools.map(t => ({ ...t, section: s.label })));

const ToolCard = ({ t, i }) => {
  const navigate = useNavigate();
  const Icon = t.icon;
  const isComingSoon = t.comingSoon === true;
  return (
    <motion.button
      onClick={() => { if (!isComingSoon) navigate(t.path); }}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
      data-testid={`tool-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`text-left p-4 sm:p-5 rounded-xl transition-all group ${isComingSoon ? '' : 'hover:ring-1 hover:ring-white/10'} ${t.featured ? 'col-span-2 lg:col-span-3' : ''}`}
      style={{
        background: t.featured ? 'rgba(200,240,96,0.04)' : '#141414',
        border: `1px solid ${t.featured ? 'rgba(200,240,96,0.18)' : 'rgba(255,255,255,0.06)'}`,
        opacity: isComingSoon ? 0.55 : 1,
        cursor: isComingSoon ? 'not-allowed' : 'pointer',
      }}
    >
      {/* Visual preview of what this tool produces */}
      {t.preview && !isComingSoon && (
        <div className="mb-3 transition-transform group-hover:scale-[1.02]" data-testid={`preview-${t.preview}`}>
          <ToolPreview id={t.preview} />
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.1)' }}>
          <Icon size={18} style={{ color: '#c8f060' }} />
        </div>
        {isComingSoon ? (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
            style={{ background: '#1a1a1a', color: 'rgba(245,240,232,0.35)', border: '1px solid rgba(255,255,255,0.06)' }}>
            Coming soon
          </span>
        ) : t.pro ? (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
            style={{ background: '#1a1a1a', color: '#c8f060', border: '1px solid rgba(200,240,96,0.18)' }}>
            PRO
          </span>
        ) : t.credits && (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)' }}>
            {t.credits} cr
          </span>
        )}
      </div>
      <p className="text-sm font-semibold mb-0.5" style={{ color: '#f5f0e8' }}>{t.label}</p>
      <p className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>{t.desc}</p>
    </motion.button>
  );
};

export default function CreateHubPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();

  // Search mode flattens everything into one grid
  if (q) {
    const filtered = ALL_TOOLS.filter(t => t.label.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="create-hub">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif" }}>What do you want to make today?</h1>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(245,240,232,0.25)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tools..." data-testid="create-search" autoFocus
              className="w-full pl-10 pr-4 py-3 rounded-xl text-sm bg-transparent outline-none"
              style={{ border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((t, i) => <ToolCard key={t.path} t={t} i={i} />)}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <PenNib size={32} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No tools match "{search}"</p>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="create-hub">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif" }}>What do you want to make today?</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>Fifteen tools, grouped by what you need.</p>
        </div>

        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(245,240,232,0.25)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tools..." data-testid="create-search"
            className="w-full pl-10 pr-4 py-3 rounded-xl text-sm bg-transparent outline-none"
            style={{ border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
        </div>

        {/* Category groups */}
        {SECTIONS.map((section, sIdx) => (
          <div key={section.id} data-testid={`category-${section.id}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <h2 className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: '#c8f060' }}>{section.label}</h2>
              <p className="text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>{section.sub}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {section.tools.map((t, i) => <ToolCard key={t.path} t={t} i={sIdx * 3 + i} />)}
            </div>
          </div>
        ))}

        {/* Coming soon */}
        <div>
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: 'rgba(245,240,232,0.4)' }}>Coming soon</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {COMING_SOON.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={t.path} className="text-left p-4 sm:p-5 rounded-xl opacity-50" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <Icon size={20} style={{ color: 'rgba(245,240,232,0.4)' }} />
                    </div>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-testid="coming-soon-badge"
                      style={{ background: 'rgba(255,180,0,0.08)', color: '#fbbf24', border: '1px solid rgba(255,180,0,0.18)' }}>
                      Coming soon
                    </span>
                  </div>
                  <p className="text-sm font-semibold mb-0.5" style={{ color: '#f5f0e8' }}>{t.label}</p>
                  <p className="text-xs" style={{ color: 'rgba(245,240,232,0.35)' }}>{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
